import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Zap, Loader2, Eye, EyeOff, CheckCircle2, XCircle } from "lucide-react";
import axios from "axios";
import { API } from "@/App";

export default function SignupPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    full_name: "",
    company_name: ""
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false
  });

  const checkPasswordStrength = (password) => {
    setPasswordStrength({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password)
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (name === "password") {
      checkPasswordStrength(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    const allStrengthChecks = Object.values(passwordStrength).every(v => v);
    if (!allStrengthChecks) {
      setError("Password does not meet requirements");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/customer/auth/signup`, {
        email: formData.email,
        password: formData.password,
        full_name: formData.full_name || null,
        company_name: formData.company_name || null
      });

      if (response.data.success) {
        // Redirect to verification page
        navigate('/verify-email', { state: { email: formData.email } });
      } else {
        setError(response.data.error || "Signup failed");
      }
    } catch (err) {
      console.error("Signup error:", err);
      setError(err.response?.data?.error || err.response?.data?.detail || "Signup failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const PasswordRequirement = ({ met, text }) => (
    <div className="flex items-center gap-2 text-sm">
      {met ? (
        <CheckCircle2 className="w-4 h-4 text-green-400" />
      ) : (
        <XCircle className="w-4 h-4 text-slate-500" />
      )}
      <span className={met ? "text-green-400" : "text-slate-500"}>{text}</span>
    </div>
  );

  return (
    <div className="min-h-screen gradient-hero flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="glass-heavy rounded-3xl p-8 border border-white/10">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-6">
              <div className="w-12 h-12 rounded-xl gradient-active flex items-center justify-center">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-heading font-semibold text-white">QuantumFlowSync</span>
            </div>
            <h1 className="text-3xl font-heading font-semibold text-white mb-2">Create your account</h1>
            <p className="text-slate-300">Start automating workflows in minutes</p>
          </div>

          {error && (
            <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-white">Email *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-slate-500"
                placeholder="you@company.com"
              />
            </div>

            <div>
              <Label htmlFor="full_name" className="text-white">Full Name (Optional)</Label>
              <Input
                id="full_name"
                name="full_name"
                type="text"
                value={formData.full_name}
                onChange={handleChange}
                className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-slate-500"
                placeholder="John Doe"
              />
            </div>

            <div>
              <Label htmlFor="company_name" className="text-white">Company Name (Optional)</Label>
              <Input
                id="company_name"
                name="company_name"
                type="text"
                value={formData.company_name}
                onChange={handleChange}
                className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-slate-500"
                placeholder="Acme Inc."
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-white">Password *</Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="bg-white/5 border-white/10 text-white pr-10 placeholder:text-slate-500"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              
              <div className="mt-3 space-y-1">
                <PasswordRequirement met={passwordStrength.length} text="At least 8 characters" />
                <PasswordRequirement met={passwordStrength.uppercase} text="One uppercase letter" />
                <PasswordRequirement met={passwordStrength.lowercase} text="One lowercase letter" />
                <PasswordRequirement met={passwordStrength.number} text="One number" />
              </div>
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-white">Confirm Password *</Label>
              <div className="relative mt-1">
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  className="bg-white/5 border-white/10 text-white pr-10 placeholder:text-slate-500"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-full gradient-active hover:opacity-90 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Creating account...
                </>
              ) : (
                "Create Account"
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-slate-400 text-sm">
              Already have an account?{" "}
              <Link to="/login" className="text-blue-400 hover:text-blue-300 transition-colors">
                Sign in
              </Link>
            </p>
          </div>

          <div className="mt-6 text-center">
            <p className="text-slate-500 text-xs">
              By signing up, you agree to our{" "}
              <a href="/terms" className="underline hover:text-slate-400">Terms of Service</a>
              {" "}and{" "}
              <a href="/privacy" className="underline hover:text-slate-400">Privacy Policy</a>
            </p>
          </div>
        </div>

        <div className="text-center mt-6">
          <button 
            onClick={() => navigate('/')} 
            className="text-slate-400 hover:text-white transition-colors text-sm"
          >
            ← Back to home
          </button>
        </div>
      </div>
    </div>
  );
}
